import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { buildClientWorkspaceOwnershipScope, clientWorkspaceOwnsEntity } from '~/lib/clientWorkspaceDetail';
import { toMillis } from '~/lib/timestamps';
import type { Approval, Client, FormSubmission, Invoice, OnboardingRun, Project, Quote, Task } from '~/types/models';

export function useClientWorkspaceEntities(options: {
    client: MaybeRefOrGetter<Client | null>;
    projectStore: any;
    taskStore: any;
    onboardingStore: any;
    quotesStore: any;
    invoicesStore: any;
    approvalsStore: any;
    formStore: any;
}) {
    const clientId = computed(() => String(toValue(options.client)?.id || '').trim());
    const ownershipScope = computed(() => buildClientWorkspaceOwnershipScope({
        clientId: clientId.value,
        linkedProjectIds: options.projectStore.projects
            .filter((project: Project) => String(project.clientId || '').trim() === clientId.value)
            .map((project: Project) => project.id),
    }));
    const owns = (entityClientId?: string, entityProjectId?: string) => clientWorkspaceOwnsEntity({
        scope: ownershipScope.value,
        entityClientId,
        entityProjectId,
    });

    const relatedProjects = computed<Project[]>(() => clientId.value
        ? options.projectStore.projects
            .filter((project: Project) => owns(project.clientId, project.id))
            .sort((left: Project, right: Project) => toMillis(right.updatedAt) - toMillis(left.updatedAt))
        : []);
    const relatedProjectIds = computed(() => relatedProjects.value.map(project => project.id));
    const relatedTasks = computed<Task[]>(() => clientId.value
        ? options.taskStore.tasks.filter((task: Task) => owns(task.clientId, task.projectId))
        : []);
    const relatedRuns = computed<OnboardingRun[]>(() => clientId.value
        ? options.onboardingStore.runs.filter((run: OnboardingRun) => owns(run.clientId, run.projectId))
        : []);
    const relatedQuotes = computed<Quote[]>(() => clientId.value
        ? options.quotesStore.quotes
            .filter((quote: Quote) => owns(quote.clientId, quote.projectId))
            .sort((left: Quote, right: Quote) => toMillis(right.updatedAt || right.createdAt) - toMillis(left.updatedAt || left.createdAt))
        : []);
    const relatedInvoices = computed<Invoice[]>(() => clientId.value
        ? options.invoicesStore.invoices
            .filter((invoice: Invoice) => owns(invoice.clientId, invoice.projectId))
            .sort((left: Invoice, right: Invoice) => toMillis(right.updatedAt || right.createdAt) - toMillis(left.updatedAt || left.createdAt))
        : []);
    const relatedApprovals = computed<Approval[]>(() => relatedProjectIds.value
        .flatMap(projectId => options.approvalsStore.approvalsByProject[projectId] || [])
        .sort((left: Approval, right: Approval) => toMillis(right.createdAt) - toMillis(left.createdAt)));
    const relatedSubmissions = computed<FormSubmission[]>(() => clientId.value
        ? options.formStore.submissions
            .filter((submission: FormSubmission) => owns(submission.linkedClientId, submission.linkedProjectId))
            .sort((left: FormSubmission, right: FormSubmission) => toMillis(right.updatedAt || right.createdAt) - toMillis(left.updatedAt || left.createdAt))
        : []);

    const pendingTasks = computed(() => relatedTasks.value.filter(task => !['done', 'completed'].includes(String(task.status || '').toLowerCase())));
    const pendingApprovals = computed(() => relatedApprovals.value.filter(approval => approval.status === 'pending'));
    const outstandingInvoices = computed(() => relatedInvoices.value.filter(invoice => invoice.status !== 'paid' && Number(invoice.amountDue || 0) > 0));
    const activeOnboardingRuns = computed(() => relatedRuns.value.filter(run => run.status === 'active'));
    const sentClientQuotes = computed(() => relatedQuotes.value.filter(quote => quote.status === 'sent' && quote.clientFacing !== false));

    return {
        ownershipScope,
        relatedProjects,
        relatedProjectIds,
        relatedTasks,
        relatedRuns,
        relatedQuotes,
        relatedInvoices,
        relatedApprovals,
        relatedSubmissions,
        pendingTasks,
        pendingApprovals,
        outstandingInvoices,
        activeOnboardingRuns,
        sentClientQuotes,
    };
}
